/* eslint-disable import/no-cycle */
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { useFormik } from "formik";
import { isEmpty } from "lodash";
import { useSnackbar } from "notistack";
import * as React from "react";
import * as yup from "yup";
import { applicationInfoType } from "types/app";
import API from "@/api/serverCall";
import getOrganizationInfoRequestThunk from "@/redux/organization/thunk";
import { useDispatch } from "react-redux";
import {
	DialogActions,
	FormControl,
	MenuItem,
	// MobileStepper,
	Select,
} from "@mui/material";
import { postContactUs } from "@/views/Price/api";
import { useTranslation } from "react-i18next";

interface ICreateAppDialogProps {
	openModal: boolean;
	setModalOpen: (value: boolean) => void;
	handleModalClose: () => void;
	applicationInfo: applicationInfoType[];
	setApplicationInfo: (value: applicationInfoType[]) => void;
	defaultEmail: string;
}

/* interface InitialMailValues {
	title: string;
	content: string;
} */

type appInfoType = {
	id: number;
	name: string;
	packageAndroid: string;
	packageIos: string;
	isFoodLens: boolean | number;
};

const CreateAppDialog: React.FC<ICreateAppDialogProps> = ({
	openModal,
	setModalOpen,
	handleModalClose,
	applicationInfo,
	setApplicationInfo,
	defaultEmail,
}) => {
	const dispatch = useDispatch();
	const { t, i18n } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [activeStep, setActiveStep] = React.useState(0);
	const emailRef = React.useRef<HTMLInputElement>();
	const contentRef = React.useRef<HTMLInputElement>();
	const [createAppText, setCreateAppText] = React.useState<string>("");
	const registApp = (appInfo: appInfoType) => {
		// for loading
		try {
			const requestData = {
				...appInfo,
				id: 0,
			};
			API.POST<applicationInfoType>({
				link: `/app`, // 두잉랩서버 로그인토큰발급 api임
				body: requestData,
			})
				.then((response) => {
					if (!isEmpty(response.data)) {
						setApplicationInfo([...applicationInfo, response.data]);
						dispatch(getOrganizationInfoRequestThunk());
						// setModalOpen(false);
						enqueueSnackbar(t("application_create.success"), {
							variant: "info",
						});
						setCreateAppText(`
- 생성된 앱 이름: ${requestData.name}<br>
- 패키지명(Android): ${requestData.packageAndroid}<br>
- 패키지명(iOS): ${requestData.packageIos}<br>
- 사용 API: ${requestData.isFoodLens ? "FoodLens" : "CaloAI"}<br>
						`);
						sendEmail();
						setActiveStep(1);
						handleModalClose();
					}
				})
				.catch((error) => {
					if (error.response.status === 409) {
						enqueueSnackbar(
							t("application_create.validation_pacakage_name"),
							{
								variant: "info",
							},
						);
					} else if (error.response.status === 411) {
						enqueueSnackbar(t("application_create.validation_max_app"), {
							variant: "info",
						});
					} else if (error.response.status === 400) {
						enqueueSnackbar(
							t("application_create.validation_pacakage_name_empty"),
							{
								variant: "info",
							},
						);
					} else {
						enqueueSnackbar(t("application_create.validation_etc"), {
							variant: "info",
						});
					}
				});
		} catch (error) {
			console.error(error);
		}
	};

	const validationSchema = yup.object({
		name: yup.string().trim().required(t("application_create.validation_app_name")),
	});

	const formik = useFormik({
		initialValues: {
			id: 0,
			name: "",
			packageAndroid: "",
			packageIos: "",
			isFoodLens: 1,
		},
		validationSchema,
		onSubmit: (values) => {
			registApp({
				id: 0,
				name: values.name,
				packageAndroid: values.packageAndroid,
				packageIos: values.packageIos,
				isFoodLens: Boolean(values.isFoodLens),
			});
		},
	});
	React.useEffect(() => {
		formik.resetForm();
	}, [openModal]);

	const sendEmail = async () => {
		try {
			await postContactUs({
				contents: createAppText,
				title: "[세일즈 플랫폼] 앱 생성 알림",
				email: defaultEmail,
				manager: "",
				type: "makeApp",
			});
		} catch (e) {
			/* enqueueSnackbar("메일 발송중 오류가 발생했습니다.", {
				variant: "error",
			}); */
		} finally {
			setModalOpen(false);
		}
		/* console.log(emailRef.current.value);
		console.log(contentRef.current.value);
		console.log(formik.values); */
	};
	return (
		<Dialog onClose={handleModalClose} open={openModal} fullWidth={true}>
			<form onSubmit={formik.handleSubmit}>
				<DialogTitle
					sx={{
						fontSize: "2.75rem",
						fontWeight: 700,
						textAlign: "center",
						marginTop: "2rem",
					}}
				>
					{t("application_create.title")}
				</DialogTitle>

				<DialogContent sx={{ padding: "3rem 3rem 2rem 3rem" }}>
					<div style={{ marginTop: "2rem" }}>
						<div
							style={{
								fontSize: "1.2rem",
								marginBottom: "0.5rem",
							}}
						>
							{t("application_create.app_name_title")}
						</div>
						<TextField
							variant="outlined"
							size="medium"
							fullWidth={true}
							id="name"
							name="name"
							placeholder={t("application_create.app_name_placeholder")}
							value={formik.values.name}
							onChange={formik.handleChange}
							error={formik.touched.name && Boolean(formik.errors.name)}
							helperText={formik.touched.name && formik.errors.name}
						/>
					</div>
					<div style={{ marginTop: "3rem" }}>
						<div
							style={{
								fontSize: "1.2rem",
								marginBottom: "0.5rem",
							}}
						>
							{t("application_create.package_name_title")}
						</div>
						<div
							style={{
								fontSize: "0.9rem",
								lineHeight: "1.125rem",
								opacity: 0.5,
							}}
						>
							{t("application_create.package_name_guide")}
						</div>
						<div style={{ display: "flex", gap: "0.25rem" }}>
							<div
								style={{
									alignItems: "center",
									marginTop: "1rem",
									width: "50%",
								}}
							>
								<span
									style={{
										display: "block",
										marginRight: "2rem",
									}}
								>
									Android
								</span>
								<TextField
									variant="outlined"
									size="medium"
									fullWidth={true}
									id="packageAndroid"
									name="packageAndroid"
									placeholder="Package Name"
									value={formik.values.packageAndroid}
									onChange={formik.handleChange}
									error={
										formik.touched.packageAndroid &&
										Boolean(formik.errors.packageAndroid)
									}
									helperText={
										formik.touched.packageAndroid &&
										formik.errors.packageAndroid
									}
								/>
							</div>
							<div
								style={{
									alignItems: "center",
									marginTop: "1rem",
									width: "50%",
								}}
							>
								<span
									style={{
										display: "block",
										marginRight: "2rem",
									}}
								>
									iOS
								</span>
								<TextField
									variant="outlined"
									size="medium"
									fullWidth={true}
									id="packageIos"
									name="packageIos"
									placeholder="Bundle Identifier"
									value={formik.values.packageIos}
									onChange={formik.handleChange}
									error={
										formik.touched.packageIos &&
										Boolean(formik.errors.packageIos)
									}
									helperText={
										formik.touched.packageIos &&
										formik.errors.packageIos
									}
								/>
							</div>
						</div>
					</div>
					<div style={{ marginTop: "3rem" }}>
						<span
							style={{
								marginBottom: "0.5rem",
								display: "block",
							}}
						>
							{t("application_create.api_title")}
						</span>
						<FormControl fullWidth>
							<Select
								labelId="api-select-label"
								id="demo-simple-select"
								value={formik.values.isFoodLens}
								onChange={(selectedOption) => {
									formik.setFieldValue(
										"isFoodLens",
										selectedOption.target.value,
									);
								}}
							>
								<MenuItem value={1}>FoodLens 1.0</MenuItem>
								<MenuItem value={0}>CaloAI (FoodLens 2.0)</MenuItem>
							</Select>
						</FormControl>
					</div>
				</DialogContent>
				<DialogActions sx={{ marginBottom: "1rem" }}>
					<Button
						variant="outlined"
						sx={{
							color: "#747474",
							borderColor: "#747474",
						}}
						onClick={handleModalClose}
					>
						{t("application_create.cancel")}
					</Button>
					<Button
						size="small"
						type="submit"
						style={{
							backgroundColor: "#2453F9",
							color: "white",
							marginRight: "1rem",
							height: "2.5rem",
							padding: "1rem",
						}}
					>
						{t("application_create.create")}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
export default CreateAppDialog;
